"use client";

import { initI18next } from "@repo/i18next-config";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { I18nProvider } from "@repo/ui";
import React, { useEffect, useState, type ReactNode } from "react";
import { DynamicHtmlLang } from "./components/DynamicHtmlLang";
import { CookiesProvider } from "react-cookie";

interface ProvidersProps {
  children: ReactNode;
}

export function Providers({ children }: ProvidersProps) {
  const [isInitialized, setIsInitialized] = useState(false);
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const initI18n = async () => {
      try {
        await initI18next({
          backend: {
            loadPath: "/locales/{{lng}}/{{ns}}.json",
          },
        });
        setIsInitialized(true);
        
        // Ensure language persistence is synced after initialization
        if (typeof window !== "undefined") {
          const storedLang = localStorage.getItem("i18nextLng");
          if (storedLang) {
            // Also store in the API language key for consistency
            localStorage.setItem("esr-api-language", storedLang);
          }
        }
      } catch (error) {
        console.error("Failed to initialize i18next:", error);
      }
    };

    initI18n();
  }, []);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 5 * 60 * 1000, // 5 minutes
            gcTime: 30 * 60 * 1000, // 30 minutes
            retry: 2,
            refetchOnWindowFocus: true,
          },
        },
      })
  );

  if (!isInitialized || !isClient) {
    return null;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CookiesProvider defaultSetOptions={{ path: "/" }}>
      <I18nProvider>
        <DynamicHtmlLang />
        {children}
        <ReactQueryDevtools initialIsOpen={false} />
      </I18nProvider>
      </CookiesProvider>
    </QueryClientProvider>
  );
}